import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatZAR, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function Admin() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("dashboard");
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    if (!loading) {
      if (!user || user === false) navigate("/login");
      else if (user.role !== "admin") navigate("/account");
    }
  }, [user, loading, navigate]);

  const loadAll = async () => {
    try {
      const [s, p, o] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/products", { params: { limit: 500 } }),
        api.get("/admin/orders"),
      ]);
      setStats(s.data); setProducts(p.data); setOrders(o.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { if (user?.role === "admin") loadAll(); }, [user]);

  if (loading || !user || user === false || user.role !== "admin") {
    return <div className="px-6 py-20 text-center small-label">Verifying admin…</div>;
  }

  const updateOrderStatus = async (id, status) => {
    try {
      await api.put(`/admin/orders/${id}`, null, { params: { fulfillment_status: status } });
      toast.success("Order updated");
      loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveProduct = async (e) => {
    e.preventDefault();
    if (!editing) return;
    try {
      const payload = { ...editing, price: Number(editing.price), stock: Number(editing.stock || 25) };
      if (editing._isNew) {
        delete payload._isNew;
        await api.post("/products", payload);
        toast.success("Product created");
      } else {
        const { slug, _isNew, ...rest } = payload;
        await api.put(`/products/${slug}`, rest);
        toast.success("Product updated");
      }
      setEditing(null);
      loadAll();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const deleteProduct = async (slug) => {
    if (!confirm(`Delete ${slug}?`)) return;
    try {
      await api.delete(`/products/${slug}`);
      toast.success("Product deleted");
      loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="admin-page" className="max-w-7xl mx-auto px-6 py-12">
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="small-label">Admin</p>
          <h1 className="font-serif text-3xl sm:text-4xl mt-2">Maison Luxe HQ</h1>
        </div>
        <Link to="/" className="text-xs uppercase tracking-luxe underline">View store</Link>
      </div>

      <nav className="flex gap-2 mb-8 border-b border-stone-line">
        {["dashboard", "products", "orders"].map((t) => (
          <button
            key={t}
            data-testid={`admin-tab-${t}`}
            onClick={() => setTab(t)}
            className={`px-4 py-3 text-xs uppercase tracking-luxe border-b-2 ${tab === t ? "border-charcoal text-charcoal" : "border-transparent text-stone"}`}
          >
            {t}
          </button>
        ))}
      </nav>

      {tab === "dashboard" && stats && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat label="Revenue" value={formatZAR(stats.total_revenue)} />
          <Stat label="Orders" value={stats.total_orders} />
          <Stat label="Products" value={stats.total_products} />
          <Stat label="Customers" value={stats.total_users} />
        </div>
      )}

      {tab === "products" && (
        <div>
          <div className="flex justify-end mb-4">
            <button
              data-testid="admin-new-product-button"
              onClick={() => setEditing({
                _isNew: true, slug: "", title: "", description: "", image: "",
                category: "straight", texture: "Straight", length_inches: 20,
                lace_type: "Front Lace", density: "180%", cap_size: "Medium",
                colors: ["Natural Black"], hair_type: "Human Blend", badges: [],
                price: 1499, compare_at_price: 1999, stock: 25, featured: false, best_seller: false,
              })}
              className="btn-primary"
            >
              + New Product
            </button>
          </div>
          <div className="overflow-x-auto border border-stone-line rounded-2xl">
            <table className="w-full text-sm">
              <thead className="bg-nude text-left">
                <tr><th className="p-3">Product</th><th className="p-3">Category</th><th className="p-3">Price</th><th className="p-3">Stock</th><th className="p-3"></th></tr>
              </thead>
              <tbody>
                {products.map((p) => (
                  <tr key={p.id} className="border-t border-stone-line">
                    <td className="p-3 flex items-center gap-3">
                      <img src={p.image} alt="" className="w-10 h-12 object-cover rounded-md" />
                      <span>{p.title}</span>
                    </td>
                    <td className="p-3">{p.category}</td>
                    <td className="p-3">{formatZAR(p.price)}</td>
                    <td className="p-3">{p.stock ?? 25}</td>
                    <td className="p-3 text-right space-x-3">
                      <button data-testid={`admin-edit-${p.slug}`} onClick={() => setEditing({ ...p })} className="text-xs uppercase tracking-luxe underline">Edit</button>
                      <button data-testid={`admin-delete-${p.slug}`} onClick={() => deleteProduct(p.slug)} className="text-xs uppercase tracking-luxe text-red-700">Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "orders" && (
        <div className="overflow-x-auto border border-stone-line rounded-2xl">
          <table className="w-full text-sm">
            <thead className="bg-nude text-left">
              <tr><th className="p-3">Order</th><th className="p-3">Customer</th><th className="p-3">Total</th><th className="p-3">Status</th><th className="p-3"></th></tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id} className="border-t border-stone-line">
                  <td className="p-3">{o.order_number}<br /><span className="small-label">{new Date(o.created_at).toLocaleDateString("en-ZA")}</span></td>
                  <td className="p-3">{o.customer.first_name} {o.customer.last_name}<br /><span className="small-label">{o.customer.email}</span></td>
                  <td className="p-3">{formatZAR(o.total)}</td>
                  <td className="p-3 capitalize">{o.fulfillment_status}</td>
                  <td className="p-3 text-right">
                    <select
                      data-testid={`admin-order-status-${o.id}`}
                      value={o.fulfillment_status}
                      onChange={(e) => updateOrderStatus(o.id, e.target.value)}
                      className="text-xs bg-ivory border border-stone-line rounded-full px-3 py-2"
                    >
                      {["processing", "shipped", "delivered", "cancelled"].map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-charcoal/40 backdrop-blur-sm flex items-center justify-center p-4">
          <form onSubmit={saveProduct} className="bg-ivory rounded-3xl w-full max-w-2xl max-h-[85vh] overflow-y-auto p-8 space-y-3">
            <div className="flex justify-between items-center mb-2">
              <p className="font-serif text-2xl">{editing._isNew ? "New" : "Edit"} product</p>
              <button type="button" onClick={() => setEditing(null)} className="small-label">Close</button>
            </div>
            <Field label="Slug" disabled={!editing._isNew} value={editing.slug} onChange={(v) => setEditing({ ...editing, slug: v })} />
            <Field label="Title" value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })} />
            <Field label="Image URL" value={editing.image} onChange={(v) => setEditing({ ...editing, image: v })} />
            <Field label="Description" textarea value={editing.description} onChange={(v) => setEditing({ ...editing, description: v })} />
            <div className="grid grid-cols-2 gap-3">
              <Field label="Category" value={editing.category} onChange={(v) => setEditing({ ...editing, category: v })} />
              <Field label="Texture" value={editing.texture} onChange={(v) => setEditing({ ...editing, texture: v })} />
              <Field label="Length (inches)" type="number" value={editing.length_inches} onChange={(v) => setEditing({ ...editing, length_inches: Number(v) })} />
              <Field label="Density" value={editing.density} onChange={(v) => setEditing({ ...editing, density: v })} />
              <Field label="Lace Type" value={editing.lace_type} onChange={(v) => setEditing({ ...editing, lace_type: v })} />
              <Field label="Hair Type" value={editing.hair_type} onChange={(v) => setEditing({ ...editing, hair_type: v })} />
              <Field label="Price (ZAR)" type="number" value={editing.price} onChange={(v) => setEditing({ ...editing, price: Number(v) })} />
              <Field label="Compare at price" type="number" value={editing.compare_at_price || 0} onChange={(v) => setEditing({ ...editing, compare_at_price: Number(v) })} />
              <Field label="Stock" type="number" value={editing.stock ?? 25} onChange={(v) => setEditing({ ...editing, stock: Number(v) })} />
            </div>
            <div className="flex gap-4 text-sm">
              <label><input type="checkbox" checked={!!editing.featured} onChange={(e) => setEditing({ ...editing, featured: e.target.checked })} /> Featured</label>
              <label><input type="checkbox" checked={!!editing.best_seller} onChange={(e) => setEditing({ ...editing, best_seller: e.target.checked })} /> Best seller</label>
            </div>
            <button type="submit" data-testid="admin-save-product" className="btn-primary w-full">Save</button>
          </form>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-nude rounded-3xl p-6">
      <p className="small-label">{label}</p>
      <p className="font-serif text-3xl mt-2">{value}</p>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", textarea = false, disabled = false }) {
  return (
    <label className="block text-sm">
      <span className="small-label">{label}</span>
      {textarea ? (
        <textarea rows={4} value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full bg-ivory border border-stone-line rounded-2xl px-4 py-3 text-sm" />
      ) : (
        <input type={type} value={value ?? ""} disabled={disabled} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full bg-ivory border border-stone-line rounded-2xl px-4 py-3 text-sm disabled:bg-nude" />
      )}
    </label>
  );
}
